import { useState } from 'react'
import { X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { useCurrentActivity } from '@/hooks/use-current-activity'
import { localInputValueToDate } from '@/lib/time-input'
import { selectSelectedActivity, useActivityStore } from '@/store/activity-store'

export interface SplitDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Picks one or more split points and calls `splitSelectedAtTimestamps`.
 * Deliberately non-modal with no overlay and positioned in a corner (see
 * `ui/dialog.tsx`'s `withoutOverlay`) -- unlike every other dialog this
 * milestone, split needs the user to be able to click directly on the
 * timeline *while this dialog stays open*, since clicking the timeline is
 * one of its three independent ways to add a split point (the others
 * being lap-boundary quick-buttons and manual timestamp entry).
 */
export function SplitDialog({ open, onOpenChange }: SplitDialogProps) {
  const loaded = useActivityStore(selectSelectedActivity)
  const splitPickMode = useActivityStore((s) => s.splitPickMode)
  const setSplitPickMode = useActivityStore((s) => s.setSplitPickMode)
  const splitPointsMs = useActivityStore((s) => s.splitPointsMs)
  const addSplitPoint = useActivityStore((s) => s.addSplitPoint)
  const removeSplitPoint = useActivityStore((s) => s.removeSplitPoint)
  const clearSplitPoints = useActivityStore((s) => s.clearSplitPoints)
  const splitSelectedAtTimestamps = useActivityStore((s) => s.splitSelectedAtTimestamps)

  const [manualValue, setManualValue] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Only fetches while actually open -- see `useCurrentActivity`'s doc
  // comment for why (this dialog, like every edit dialog, stays mounted
  // while closed). Degrades gracefully while loading: the lap quick-add
  // section below just shows nothing until `lapStarts` is populated,
  // rather than blocking the timeline-click/manual-entry split-point
  // pickers, which don't need the activity at all.
  const { result } = useCurrentActivity(open ? loaded?.history : undefined)

  if (loaded == null) return null

  const lapStarts = (result?.activity.laps ?? [])
    .map((lap) => (lap.startTime instanceof Date ? lap.startTime.getTime() : null))
    .filter((t): t is number => t != null)

  const handleAddManual = () => {
    const date = localInputValueToDate(manualValue)
    if (date != null) addSplitPoint(date.getTime())
    setManualValue('')
  }

  const handleClose = (next: boolean) => {
    if (!next) setSplitPickMode(false)
    onOpenChange(next)
  }

  const handleConfirm = async () => {
    if (splitPointsMs.length === 0) return
    setSubmitting(true)
    try {
      // `null` means the split failed (the store already surfaced a
      // toast) -- keep the dialog open with the user's split points
      // intact rather than discarding their work on a failure they'd
      // otherwise have no way to tell happened.
      const newIds = await splitSelectedAtTimestamps(splitPointsMs.map((ms) => new Date(ms)))
      if (newIds != null) {
        clearSplitPoints()
        setSplitPickMode(false)
        onOpenChange(false)
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose} modal={false}>
      <DialogContent
        withoutOverlay
        onInteractOutside={(e) => e.preventDefault()}
        aria-describedby="split-description"
        // Not actually modal (see this component's doc comment and the
        // parent `Dialog`'s `modal={false}`) -- background content
        // (the timeline) stays genuinely interactive while this is open,
        // so `aria-modal="true"` (the shared `DialogContent` default)
        // would misrepresent that to assistive tech.
        aria-modal="false"
        className="top-20 right-4 left-auto max-w-sm translate-x-0 translate-y-0"
      >
        <DialogTitle>Split activity</DialogTitle>
        <DialogDescription id="split-description">
          Pick one or more points to split this activity at. Each resulting segment is added to the
          sidebar as its own new, independently editable file -- the original is not changed.
        </DialogDescription>

        <div className="flex flex-col gap-3">
          <section>
            <h3 className="text-foreground mb-1 text-xs font-semibold">Split points</h3>
            {splitPointsMs.length === 0 ? (
              <p className="text-muted-foreground text-xs">None selected yet.</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {splitPointsMs.map((ms) => (
                  <li
                    key={ms}
                    className="border-border bg-background flex items-center justify-between gap-2 rounded-md border px-2 py-1 text-xs"
                  >
                    <span className="text-foreground">{new Date(ms).toLocaleString()}</span>
                    <button
                      type="button"
                      aria-label={`Remove split point at ${new Date(ms).toLocaleString()}`}
                      onClick={() => removeSplitPoint(ms)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="flex flex-col gap-1">
            <h3 className="text-foreground text-xs font-semibold">Add from the timeline</h3>
            <Button
              type="button"
              size="sm"
              variant={splitPickMode ? 'default' : 'outline'}
              aria-pressed={splitPickMode}
              onClick={() => setSplitPickMode(!splitPickMode)}
            >
              {splitPickMode ? 'Click the timeline to add a point…' : 'Add from timeline'}
            </Button>
          </section>

          {lapStarts.length > 0 && (
            <section className="flex flex-col gap-1">
              <h3 className="text-foreground text-xs font-semibold">Add a lap boundary</h3>
              <div className="flex flex-wrap gap-1">
                {lapStarts.map((ms, i) => (
                  <button
                    key={ms}
                    type="button"
                    disabled={splitPointsMs.includes(ms)}
                    onClick={() => addSplitPoint(ms)}
                    className="border-border text-foreground hover:bg-accent hover:text-accent-foreground rounded-md border px-2 py-0.5 text-xs outline-none disabled:opacity-40"
                  >
                    Lap {i + 1}
                  </button>
                ))}
              </div>
            </section>
          )}

          <section className="flex flex-col gap-1">
            <h3 className="text-foreground text-xs font-semibold">Add manually</h3>
            <div className="flex items-end gap-2">
              <input
                type="datetime-local"
                step={1}
                value={manualValue}
                onChange={(e) => setManualValue(e.target.value)}
                aria-label="Split point timestamp"
                className="border-input bg-background text-foreground focus-visible:ring-ring flex-1 rounded-md border px-2 py-1 text-sm outline-none focus-visible:ring-2"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={localInputValueToDate(manualValue) == null}
                onClick={handleAddManual}
              >
                Add
              </Button>
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={splitPointsMs.length === 0 || submitting}
            onClick={() => void handleConfirm()}
          >
            Split into {splitPointsMs.length + 1} activities
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
